import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Header, Button, Checkbox, Label, Message, Loader, Container, List, Icon } from 'semantic-ui-react';
import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PosterAvatar } from '../../../components/PosterAvatar';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
// Same look as the review queue — reused directly rather than cloned.
import styles from './PostReviews.module.css';
import pageStyles from './PostsScheduled.module.css';

const COMMUNITY_GROUP_ID = 2;
// Mirrors the backend's SENT_POST_EDIT_WINDOW_MS (Post.ts) — kept in sync there, not imported,
// since app/ and backend/ are separate builds.
const SENT_POST_EDIT_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

// postsScheduled returns the Post union; fields must be selected per member.
const POST_LIST_FIELDS = `
  id
  title
  type
  createdAt
  publishAt
  archivedAt
  tags { id name groupId }
  poster { id name photoUrl }
`;

const GET_POSTS_SCHEDULED = gql`
  query PostsScheduled($mineOnly: Boolean) {
    postsScheduled(mineOnly: $mineOnly) {
      ... on CommunityUpdate { ${POST_LIST_FIELDS} }
      ... on CommunityPrayerRequest { ${POST_LIST_FIELDS} }
      ... on LeadershipUpdate { ${POST_LIST_FIELDS} }
      ... on LeadershipPrayerRequest { ${POST_LIST_FIELDS} }
      ... on Birthday { ${POST_LIST_FIELDS} }
    }
  }
`;

const GET_POSTS_SENT = gql`
  query PostsSent($mineOnly: Boolean) {
    postsSent(limit: 5, mineOnly: $mineOnly) {
      ... on CommunityUpdate { ${POST_LIST_FIELDS} }
      ... on CommunityPrayerRequest { ${POST_LIST_FIELDS} }
      ... on LeadershipUpdate { ${POST_LIST_FIELDS} }
      ... on LeadershipPrayerRequest { ${POST_LIST_FIELDS} }
      ... on Birthday { ${POST_LIST_FIELDS} }
    }
  }
`;

const TAKEDOWN_SENT_POST = gql`
  mutation TakedownSentPost($id: Int!) {
    takedownSentPost(id: $id) {
      ... on CommunityUpdate { id archivedAt }
      ... on CommunityPrayerRequest { id archivedAt }
      ... on LeadershipUpdate { id archivedAt }
      ... on LeadershipPrayerRequest { id archivedAt }
      ... on Birthday { id archivedAt }
    }
  }
`;

const RESTORE_SENT_POST = gql`
  mutation RestoreSentPost($id: Int!) {
    restoreSentPost(id: $id) {
      ... on CommunityUpdate { id archivedAt }
      ... on CommunityPrayerRequest { id archivedAt }
      ... on LeadershipUpdate { id archivedAt }
      ... on LeadershipPrayerRequest { id archivedAt }
      ... on Birthday { id archivedAt }
    }
  }
`;

const PostsScheduledView: React.FC = () => {
    // Redirect gate, not just a hidden nav item — this page didn't exist before, so unlike
    // PostReviews it's worth defending in depth rather than relying on the backend's 403 alone.
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const navigate = useNavigate();
    const [mineOnly, setMineOnly] = useState(false);
    const { data, loading, error } = useQuery(GET_POSTS_SCHEDULED, {
        variables: { mineOnly },
        fetchPolicy: 'network-only',
    });
    const { data: sentData, loading: sentLoading, error: sentError, refetch: refetchSent } = useQuery(GET_POSTS_SENT, {
        variables: { mineOnly },
        fetchPolicy: 'network-only',
    });
    const [takedownSentPost, { loading: takedownLoading }] = useMutation(TAKEDOWN_SENT_POST);
    const [restoreSentPost, { loading: restoreLoading }] = useMutation(RESTORE_SENT_POST);

    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    if (loading) return <ResponsiveContainer><Loader active inline='centered' style={{ marginTop: '5em' }}>Loading scheduled posts...</Loader></ResponsiveContainer>;
    if (error) return <ResponsiveContainer><Container text><Message error content="Could not fetch scheduled posts." /></Container></ResponsiveContainer>;

    // Already ordered soonest-first by the server.
    const posts = data?.postsScheduled || [];
    // Already ordered most-recent-first by the server.
    const sentPosts = sentData?.postsSent || [];

    return (
        <ResponsiveContainer>
        <Container className={pageStyles.pageContainer}>
        <div className={pageStyles.layout}>
        <div className={pageStyles.main}>
            <div className={styles.headerSection}>
                <Header as="h1">Scheduled Posts</Header>
                <Label color="blue" size="tiny">{posts.length} Scheduled</Label>
                <Checkbox
                    toggle
                    label="Only mine"
                    checked={mineOnly}
                    onChange={() => setMineOnly((prev) => !prev)}
                    style={{ marginLeft: 'auto' }}
                />
            </div>

            {posts.length === 0 && (
                <Message
                    info
                    content={mineOnly ? "You don't have anything scheduled to publish yet." : 'Nothing is scheduled to publish yet.'}
                />
            )}

            <List relaxed className={styles.postList}>
                {posts.map((post: any) => {
                    const communityTags = post.tags?.filter((t: any) => t.groupId === COMMUNITY_GROUP_ID) || [];
                    const otherTags = post.tags?.filter((t: any) => t.groupId !== COMMUNITY_GROUP_ID) || [];
                    const hasCommunities = communityTags.length > 0;
                    const publishDate = parseApiDate(post.publishAt);

                    return (
                        <List.Item
                            key={post.id}
                            className={styles.postItem}
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/posts/scheduled/${post.id}`)}
                        >
                            {/* LEFT: Identity & Categorization */}
                            <div className={styles.leftCol}>
                                <div style={{ marginBottom: '4px' }}>
                                    <Label size="mini" basic className={styles.typeBadge}>{post.type}</Label>
                                </div>
                                <div className={styles.postTitle} style={{ marginBottom: '8px' }}>{post.title}</div>

                                <div className={styles.tagSection}>
                                    {hasCommunities ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#666' }}>
                                                Communities:
                                            </span>
                                            {communityTags.map((tag: any) => (
                                                <Label key={tag.id} size="mini" color="purple" basic>
                                                    <Icon name="users" />
                                                    {tag.name}
                                                </Label>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {otherTags.map((tag: any) => (
                                                <Label key={tag.id} size="mini" color="teal" basic>
                                                    <Icon name="tag" />
                                                    {tag.name}
                                                </Label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* MIDDLE: When it goes out */}
                            <div className={styles.centerCol}>
                                <div className={styles.dateRow}>
                                    <Icon name="send" size="small" />
                                    <span>
                                        Publishes {publishDate?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                    </span>
                                </div>
                            </div>

                            {/* RIGHT: Author & edit action */}
                            <div className={styles.rightCol}>
                                <div className={styles.avatarWrapper}>
                                    <PosterAvatar photoUrl={post.poster.photoUrl} className={styles.posterAvatar} size={38} />
                                    <span className={styles.hoverName}>{post.poster.name}</span>
                                </div>
                                <Button
                                    circular
                                    icon="pencil"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/posts/scheduled/${post.id}`); }}
                                    className={styles.reviewBtn}
                                />
                            </div>
                        </List.Item>
                    );
                })}
            </List>
        </div>

        <div className={pageStyles.sidebar}>
            <Header as="h4" className={pageStyles.sidebarHeader}>Recently Sent</Header>

            {sentLoading && <Loader active inline size="small" />}
            {sentError && <Message error size="tiny" content="Could not fetch recently sent posts." />}
            {!sentLoading && !sentError && sentPosts.length === 0 && (
                <Message
                    info
                    size="tiny"
                    content={mineOnly ? "You haven't sent anything yet." : 'Nothing has been sent yet.'}
                />
            )}

            <div className={pageStyles.sentList}>
                {sentPosts.map((post: any) => {
                    const publishDate = parseApiDate(post.publishAt);
                    const isArchived = post.archivedAt != null;
                    const isEditable = !isArchived && publishDate != null
                        && (Date.now() - publishDate.getTime()) < SENT_POST_EDIT_WINDOW_MS;

                    return (
                        <div key={post.id} className={pageStyles.sentItem}>
                            <div className={pageStyles.sentTitle}>{post.title}</div>
                            <div className={pageStyles.sentTags}>
                                {(post.tags || []).map((tag: any) => (
                                    <Label
                                        key={tag.id}
                                        size="mini"
                                        basic
                                        color={tag.groupId === COMMUNITY_GROUP_ID ? 'purple' : 'teal'}
                                    >
                                        {tag.name}
                                    </Label>
                                ))}
                                {isArchived && (
                                    <Label size="mini" color="red" basic>Taken down</Label>
                                )}
                            </div>
                            <div className={pageStyles.sentDate}>
                                <Icon name="check circle outline" size="small" />
                                <span>
                                    {publishDate?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                </span>
                            </div>
                            <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
                                {isEditable && (
                                    <Button
                                        size="mini"
                                        basic
                                        icon="pencil"
                                        onClick={() => navigate(`/posts/sent/${post.id}`)}
                                    />
                                )}
                                {isArchived ? (
                                    <Button
                                        size="mini"
                                        basic
                                        color="green"
                                        loading={restoreLoading}
                                        disabled={restoreLoading}
                                        onClick={async () => {
                                            await restoreSentPost({ variables: { id: post.id } });
                                            refetchSent();
                                        }}
                                    >
                                        Restore
                                    </Button>
                                ) : (
                                    <Button
                                        size="mini"
                                        basic
                                        color="red"
                                        loading={takedownLoading}
                                        disabled={takedownLoading}
                                        onClick={async () => {
                                            await takedownSentPost({ variables: { id: post.id } });
                                            refetchSent();
                                        }}
                                    >
                                        Take down
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
        </div>
        </Container>
        </ResponsiveContainer>
    );
};

export { PostsScheduledView };
